import i18n from "@/plugins/i18n";
import {
    ACTION_GET_SURVEYS,
    ACTION_GET_SURVEY_BY_ID,
    ACTION_GET_QUESTION_TYPES,
    ACTION_GET_CONFIG,
    ACTION_PUT_CONFIG,
    ACTION_POST_CREATE_SURVEY,
    ACTION_PUT_UPDATE_SURVEY,
    ACTION_PATCH_SURVEY_STATUS,
    ACTION_DELETE_SURVEY,
    ACTION_POST_CREATE_QUESTION,
    ACTION_PUT_UPDATE_QUESTION,
    ACTION_DELETE_QUESTION,
    ACTION_REORDER_QUESTIONS,
    ACTION_POST_CREATE_OPTION,
    ACTION_PUT_UPDATE_OPTION,
    ACTION_DELETE_OPTION,
    ACTION_REORDER_OPTIONS,
    ACTION_GET_RESPONSES,
    ACTION_GET_SUMMARY,
    ACTION_GET_OVERVIEW,
    ACTION_PUT_BOT_CONFIG,
    ACTION_DELETE_BOT_CONFIG,
    SET_SURVEYS,
    SET_CURRENT_SURVEY,
    SET_QUESTION_TYPES,
    SET_SURVEY_CONFIG,
    SET_RESPONSES,
    SET_SUMMARY,
    SET_OVERVIEW,
    SET_BOT_CONFIG
} from "@/views/surveys/store/types/types.js"

import Swal from "sweetalert2";
import EventBus from '@/utils/event_bus.js';
import { SurveysService } from "../../../services";

function getSessionContext() {
    return {
        idGroup: sessionStorage.getItem("idGroup"),
        idEmpresa: sessionStorage.getItem("idEmpresa"),
        idUsuario: sessionStorage.getItem("idUsuario")
    };
}

function normalizeSurveyForBackend(survey) {
    return {
        title: survey.title || survey.name || '',
        description: survey.description || '',
        allowMultipleResponses: survey.allowMultipleResponses || false,
        validFrom: survey.validFrom || null,
        validUntil: survey.validUntil || null
    };
}

function normalizeQuestionForBackend(question) {
    const config = question.config || {};
    if (question.type === 'RATING') {
        config.scale = 5;
    }
    const payload = {
        type: question.type,
        title: question.title,
        required: question.required,
        config
    };
    const supportsOptions = ['SINGLE_CHOICE', 'MULTI_CHOICE', 'DROPDOWN'].includes(question.type);
    if (supportsOptions && Array.isArray(question.options) && question.options.length > 0) {
        payload.options = question.options.map((opt, index) => {
            if (typeof opt === 'string') {
                return { label: opt, position: index + 1 };
            }
            return { label: opt.label || '', position: opt.position || index + 1 };
        });
    }
    return payload;
}

function showSuccess(title) {
    Swal.fire({ position: 'top-end', icon: 'success', title, showConfirmButton: false, timer: 2500 });
}

function showError(message) {
    Swal.fire({ icon: "error", title: "Error", text: message || 'La acción fue rechazada' });
}

const actions = {
    [ACTION_GET_SURVEYS]: async ({ state, commit }, params = {}) => {
        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                const { idGroup, idEmpresa, idUsuario } = getSessionContext();
                SurveysService.getListSurveys(idGroup, idEmpresa, idUsuario, params)
                    .then(resp => {
                        if (resp.data && resp.data.payload) {
                            const payload = resp.data.payload;
                            const surveys = Array.isArray(payload) ? payload : (payload.content || []);
                            commit(SET_SURVEYS, surveys);
                            resolve(resp.data);
                        } else {
                            reject('Error en la acción');
                        }
                    })
                    .catch(err => { console.error('Error en la petición', err); reject(err); })
                    .finally(() => { state.loading = false; });
            } catch (error) { reject(error); }
        });
    },

    [ACTION_GET_SURVEY_BY_ID]: async ({ state, commit }, surveyId) => {
        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                const { idGroup, idEmpresa, idUsuario } = getSessionContext();
                SurveysService.getSurveyById(idGroup, idEmpresa, idUsuario, surveyId)
                    .then(resp => {
                        if (resp.data && resp.data.payload) {
                            commit(SET_CURRENT_SURVEY, resp.data.payload);
                            resolve(resp.data);
                        } else {
                            reject('Error en la acción');
                        }
                    })
                    .catch(err => { console.error('Error en la petición', err); reject(err); })
                    .finally(() => { state.loading = false; });
            } catch (error) { reject(error); }
        });
    },

    [ACTION_GET_QUESTION_TYPES]: async ({ state, commit }) => {
        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                const { idGroup, idEmpresa, idUsuario } = getSessionContext();
                SurveysService.getQuestionTypes(idGroup, idEmpresa, idUsuario)
                    .then(resp => {
                        if (resp.data && resp.data.payload) {
                            commit(SET_QUESTION_TYPES, resp.data.payload);
                            resolve(resp.data);
                        } else {
                            reject('Error en la acción');
                        }
                    })
                    .catch(err => { console.error('Error en la petición', err); reject(err); })
                    .finally(() => { state.loading = false; });
            } catch (error) { reject(error); }
        });
    },

    [ACTION_POST_CREATE_SURVEY]: ({ state }, payload) => {
        const { survey, questions, silent } = payload;
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();

        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                SurveysService.postCreateSurvey(idGroup, idEmpresa, idUsuario, normalizeSurveyForBackend(survey))
                    .then(async (resp) => {
                        if (resp.data && resp.data.payload) {
                            const createdSurvey = resp.data.payload;
                            const surveyId = createdSurvey.surveyId;
                            try {
                                for (const question of questions) {
                                    await SurveysService.postCreateQuestion(
                                        idGroup, idEmpresa, idUsuario, surveyId,
                                        normalizeQuestionForBackend(question)
                                    );
                                }
                                if (!silent) showSuccess(i18n.t('surveys.actions.createSuccess'));
                                resolve(resp.data);
                                EventBus.$emit('survey-created-successfully');
                            } catch (innerError) {
                                console.error('Error creando preguntas/opciones', innerError);
                                showError(innerError?.response?.data?.payload || 'Error al guardar las preguntas');
                                reject(innerError);
                            }
                        } else {
                            showError(resp.data && resp.data.payload);
                            reject('La acción fue rechazada');
                        }
                    })
                    .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                    .finally(() => state.loading = false);
            } catch (error) { reject(error); }
        });
    },

    [ACTION_PUT_UPDATE_SURVEY]: ({ state }, payload) => {
        const { survey, questions, originalQuestions, silent } = payload;
        const surveyId = survey.surveyId;
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();

        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                SurveysService.putUpdateSurvey(idGroup, idEmpresa, idUsuario, surveyId, normalizeSurveyForBackend(survey))
                    .then(async (resp) => {
                        if (resp.data && resp.data.payload) {
                            try {
                                if (Array.isArray(originalQuestions)) {
                                    for (const originalQuestion of originalQuestions) {
                                        if (originalQuestion.questionId) {
                                            await SurveysService.deleteQuestion(idGroup, idEmpresa, idUsuario, originalQuestion.questionId);
                                        }
                                    }
                                }
                                for (const question of questions) {
                                    await SurveysService.postCreateQuestion(
                                        idGroup, idEmpresa, idUsuario, surveyId,
                                        normalizeQuestionForBackend(question)
                                    );
                                }
                                if (!silent) showSuccess(i18n.t('surveys.actions.updateSuccess'));
                                resolve(resp.data);
                                EventBus.$emit('survey-updated-successfully');
                            } catch (innerError) {
                                console.error('Error actualizando preguntas/opciones', innerError);
                                showError(innerError?.response?.data?.payload || 'Error al guardar las preguntas');
                                reject(innerError);
                            }
                        } else {
                            showError(resp.data && resp.data.payload);
                            reject('La acción fue rechazada');
                        }
                    })
                    .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                    .finally(() => state.loading = false);
            } catch (error) { reject(error); }
        });
    },

    [ACTION_PATCH_SURVEY_STATUS]: ({ state }, payload) => {
        const { surveyId, status } = payload;
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();

        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                SurveysService.patchSurveyStatus(idGroup, idEmpresa, idUsuario, surveyId, status)
                    .then(async (resp) => {
                        if (resp.data && resp.data.payload) {
                            const title = status === 'PUBLISHED'
                                ? i18n.t('surveys.actions.publishSuccess')
                                : status === 'CLOSED'
                                    ? i18n.t('surveys.actions.finalizeSuccess')
                                    : i18n.t('surveys.actions.statusUpdatedSuccess');
                            showSuccess(title);
                            resolve(resp.data);
                            EventBus.$emit('survey-updated-successfully');
                        } else {
                            showError(resp.data && resp.data.payload);
                            reject('La acción fue rechazada');
                        }
                    })
                    .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                    .finally(() => state.loading = false);
            } catch (error) { reject(error); }
        });
    },

    [ACTION_DELETE_SURVEY]: ({ state }, surveyId) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();

        return new Promise((resolve, reject) => {
            try {
                state.loading = true;
                SurveysService.deleteSurvey(idGroup, idEmpresa, idUsuario, surveyId)
                    .then(resp => {
                        if (resp.status === 204 || (resp.data && resp.data.payload)) {
                            showSuccess(i18n.t('surveys.actions.deleteSuccess') || 'Encuesta eliminada exitosamente');
                            resolve(resp.data);
                            EventBus.$emit('survey-updated-successfully');
                        } else {
                            showError(resp.data && resp.data.payload);
                            reject('La acción fue rechazada');
                        }
                    })
                    .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                    .finally(() => state.loading = false);
            } catch (error) { reject(error); }
        });
    },

    [ACTION_POST_CREATE_QUESTION]: ({ state }, { surveyId, question }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.postCreateQuestion(idGroup, idEmpresa, idUsuario, surveyId, normalizeQuestionForBackend(question))
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_PUT_UPDATE_QUESTION]: ({ state }, { questionId, question }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.putUpdateQuestion(idGroup, idEmpresa, idUsuario, questionId, normalizeQuestionForBackend(question))
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_DELETE_QUESTION]: ({ state }, questionId) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.deleteQuestion(idGroup, idEmpresa, idUsuario, questionId)
                .then(resp => {
                    if (resp.status === 204 || (resp.data && resp.data.payload)) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_POST_CREATE_OPTION]: ({ state }, { questionId, option }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.postCreateOption(idGroup, idEmpresa, idUsuario, questionId, option)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_PUT_UPDATE_OPTION]: ({ state }, { optionId, option }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.putUpdateOption(idGroup, idEmpresa, idUsuario, optionId, option)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_DELETE_OPTION]: ({ state }, optionId) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.deleteOption(idGroup, idEmpresa, idUsuario, optionId)
                .then(resp => {
                    if (resp.status === 204 || (resp.data && resp.data.payload)) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_REORDER_QUESTIONS]: ({ state }, { surveyId, questionIds }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.putReorderQuestions(idGroup, idEmpresa, idUsuario, surveyId, questionIds)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_REORDER_OPTIONS]: ({ state }, { questionId, optionIds }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.putReorderOptions(idGroup, idEmpresa, idUsuario, questionId, optionIds)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_GET_CONFIG]: async ({ state, commit }) => {
        return new Promise((resolve, reject) => {
            state.loading = true;
            const { idGroup, idEmpresa, idUsuario } = getSessionContext();
            SurveysService.getConfig(idGroup, idEmpresa, idUsuario)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        commit(SET_SURVEY_CONFIG, resp.data.payload);
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_PUT_CONFIG]: async ({ state }, data) => {
        return new Promise((resolve, reject) => {
            state.loading = true;
            const { idGroup, idEmpresa, idUsuario } = getSessionContext();
            SurveysService.putConfig(idGroup, idEmpresa, idUsuario, data)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        showSuccess('Configuración actualizada exitosamente');
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_GET_RESPONSES]: async ({ state, commit }, { surveyId, params = {} }) => {
        return new Promise((resolve, reject) => {
            state.loading = true;
            const { idGroup, idEmpresa, idUsuario } = getSessionContext();
            SurveysService.getResponses(idGroup, idEmpresa, idUsuario, surveyId, params)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        const payload = resp.data.payload;
                        const responses = Array.isArray(payload) ? payload : (payload.content || []);
                        commit(SET_RESPONSES, responses);
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_GET_SUMMARY]: async ({ state, commit }, surveyId) => {
        return new Promise((resolve, reject) => {
            state.loading = true;
            const { idGroup, idEmpresa, idUsuario } = getSessionContext();
            SurveysService.getSummary(idGroup, idEmpresa, idUsuario, surveyId)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        commit(SET_SUMMARY, resp.data.payload);
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_GET_OVERVIEW]: async ({ state, commit }, surveyId) => {
        return new Promise((resolve, reject) => {
            state.loading = true;
            const { idGroup, idEmpresa, idUsuario } = getSessionContext();
            SurveysService.getOverview(idGroup, idEmpresa, idUsuario, surveyId)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        commit(SET_OVERVIEW, resp.data.payload);
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { console.error('Error en la petición', err); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_PUT_BOT_CONFIG]: ({ state, commit }, { surveyId, botConfig }) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.putBotConfig(idGroup, idEmpresa, idUsuario, surveyId, botConfig)
                .then(resp => {
                    if (resp.data && resp.data.payload) {
                        commit(SET_BOT_CONFIG, resp.data.payload);
                        showSuccess('Configuración del bot actualizada exitosamente');
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                .finally(() => state.loading = false);
        });
    },

    [ACTION_DELETE_BOT_CONFIG]: ({ state }, surveyId) => {
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        return new Promise((resolve, reject) => {
            state.loading = true;
            SurveysService.deleteBotConfig(idGroup, idEmpresa, idUsuario, surveyId)
                .then(resp => {
                    if (resp.status === 204 || (resp.data && resp.data.payload)) {
                        showSuccess('Configuración del bot eliminada exitosamente');
                        resolve(resp.data);
                    } else {
                        reject('Error en la acción');
                    }
                })
                .catch(err => { showError(err?.response?.data?.payload); reject(err); })
                .finally(() => state.loading = false);
        });
    }
};

export default actions;
